import type { MutableRefObject } from 'react';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import { triggerErrorSentryCheckGet } from '../../../client';
import { showAlert } from '../../../utils/telegram/alert';
import { format_num } from '../../../utils/format/format_num';
import type { UserData } from '../../../models/user';
import { useOnboardingState } from '../../../hooks/useOnboardingState';

interface MapTopBarProps {
  userData: UserData | undefined;
  showPlots: boolean;
  setShowPlots: (value: boolean) => void;
  isAdminMode: boolean;
  setIsAdminMode: (value: boolean) => void;
  sceneRef: MutableRefObject<import('../mapSceneState').MapSceneState>;
  /** Clears gold highlight on the PIXI singleton (module mutator; not React state). */
  clearMapObjectHighlight: () => void;
  closeAdminRadialMenu: () => void;
  clearAdminSelection: () => void;
  isTestingSentry: boolean;
  setIsTestingSentry: (value: boolean) => void;
  hasUnfinishedQuests: boolean;
  /** Opens the DailyQuestsModal (useMapModalStore.openModal('dailyQuests')). */
  openDailyQuestsModal: () => void;
}

/** Top bar overlay — level/currency balances (left) + Icons/God-mode toggles + quests button (right). */
export default function MapTopBar({
  userData,
  showPlots,
  setShowPlots,
  isAdminMode,
  setIsAdminMode,
  sceneRef,
  clearMapObjectHighlight,
  closeAdminRadialMenu,
  clearAdminSelection,
  isTestingSentry,
  setIsTestingSentry,
  hasUnfinishedQuests,
  openDailyQuestsModal,
}: MapTopBarProps) {
  const { t } = useTranslation();
  // Only for newcomers: after the tutorial, until the quests screen is first opened.
  const { data: onboarding } = useOnboardingState();
  const questsNeverOpened = !!onboarding?.onboarding_completed && !onboarding.quests_opened;

  const formatSoftCurrency = (value: number): string => format_num(value);

  return (
    <div
      style={{
        position: 'absolute',
        top: 20,
        left: 20,
        right: 20,
        zIndex: 1000,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        pointerEvents: 'none',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: '12px',
          flexShrink: 0,
          pointerEvents: 'none',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '6px',
            alignItems: 'flex-start',
            flexShrink: 0,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              gap: '4px',
              height: '29px',
            }}
          >
            <div
              style={{
                color: '#FFF',
                textAlign: 'left',
                WebkitTextStrokeWidth: '1.21px',
                WebkitTextStrokeColor: 'rgba(0, 0, 0, 0.60)',
                fontFamily: '"Rubik One", sans-serif',
                fontSize: '16px',
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: '29px',
                letterSpacing: '-0.747px',
                whiteSpace: 'nowrap',
              }}
            >
              Lvl: {userData?.level ?? 0}
            </div>
            <img
              src="/ui/world-map/level-up-icon.png"
              alt=""
              style={{
                width: '30.55774px',
                height: '29px',
                display: 'block',
                flexShrink: 0,
              }}
            />
          </div>
          <div
            style={{
              position: 'relative',
              width: '88px',
              height: '9px',
              flexShrink: 0,
            }}
          >
            <div
              style={{
                position: 'absolute',
                left: 0,
                top: 0,
                width: '100%',
                height: '9px',
                flexShrink: 0,
                backgroundColor: '#C2C2C2',
                borderRadius: '6px',
                border: '1px solid #7CC3B2',
                boxSizing: 'border-box',
              }}
            />
            <div
              style={{
                position: 'absolute',
                left: 0,
                top: 0,
                width: `${userData ? Math.min(100, Math.max(0, (userData.exp_current / userData.exp_to_levelup) * 100)) : 0}%`,
                height: '9px',
                borderRadius: '6px',
                backgroundColor: '#7767D7',
                border: '1px solid #6bb5a7',
                boxSizing: 'border-box',
                zIndex: 2,
              }}
            />
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            gap: '4px',
            height: '29px',
            flexShrink: 0,
            pointerEvents: 'none',
          }}
        >
          <div
            style={{
              color: '#FFF',
              textAlign: 'left',
              WebkitTextStrokeWidth: '1.21px',
              WebkitTextStrokeColor: 'rgba(0, 0, 0, 0.60)',
              fontFamily: '"Rubik One", sans-serif',
              fontSize: '16px',
              fontStyle: 'normal',
              fontWeight: 400,
              lineHeight: '29px',
              letterSpacing: '-0.747px',
              whiteSpace: 'nowrap',
            }}
          >
            {formatSoftCurrency(userData?.currency_balance ?? 0)}
          </div>
          <img
            src="/ui/world-map/shell-icon.png"
            alt=""
            style={{
              width: '25.34px',
              height: '25.34px',
              display: 'block',
              flexShrink: 0,
            }}
          />
        </div>
        {(userData?.balance_usdt ?? 0) > 0 && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: '4px',
            pointerEvents: 'none', flexShrink: 0,
          }}>
            <div style={{
              color: '#FFF',
              textAlign: 'left',
              WebkitTextStrokeWidth: '1.21px',
              WebkitTextStrokeColor: 'rgba(0, 0, 0, 0.60)',
              fontFamily: '"Rubik One", sans-serif',
              fontSize: '16px',
              fontStyle: 'normal',
              fontWeight: 400,
              lineHeight: '29px',
              letterSpacing: '-0.747px',
              whiteSpace: 'nowrap',
            }}>
              ${(userData?.balance_usdt ?? 0).toFixed(2)}
            </div>
            <img src="/ui/usdt-icon.svg" alt="" style={{ width: '25.34px', height: '25.34px', display: 'block', flexShrink: 0 }} />
          </div>
        )}
        {(userData?.balance_ton ?? 0) > 0 && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: '4px',
            pointerEvents: 'none', flexShrink: 0,
          }}>
            <div style={{
              color: '#FFF',
              textAlign: 'left',
              WebkitTextStrokeWidth: '1.21px',
              WebkitTextStrokeColor: 'rgba(0, 0, 0, 0.60)',
              fontFamily: '"Rubik One", sans-serif',
              fontSize: '16px',
              fontStyle: 'normal',
              fontWeight: 400,
              lineHeight: '29px',
              letterSpacing: '-0.747px',
              whiteSpace: 'nowrap',
            }}>
              {(userData?.balance_ton ?? 0).toFixed(4)}
            </div>
            <img src="/ui/ton-icon.svg" alt="" style={{ width: '25.34px', height: '25.34px', display: 'block', flexShrink: 0 }} />
          </div>
        )}
      </div>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: '4px',
          flexShrink: 0,
          minWidth: 0,
          marginLeft: 'auto',
          pointerEvents: 'none',
        }}
      >
        <div
          style={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            padding: '6px 24px',
            borderRadius: '50px',
            background: 'rgba(219, 219, 219, 0.3)',
            boxShadow: 'inset 4px 3px 5.9px 0px rgba(0, 0, 0, 0.12)',
            cursor: 'pointer',
            pointerEvents: 'auto',
          }}
          onClick={() => {
            setShowPlots(!showPlots);
          }}
        >
          <div
            style={{
              color: '#FFF',
              textAlign: 'center',
              WebkitTextStrokeWidth: '1.21px',
              WebkitTextStrokeColor: 'rgba(0, 0, 0, 0.60)',
              fontFamily: '"Rubik One", sans-serif',
              fontSize: '16px',
              fontStyle: 'normal',
              fontWeight: 400,
              lineHeight: '29px',
              letterSpacing: '-0.747px',
              whiteSpace: 'nowrap',
            }}
          >
            Icons
          </div>
          <div
            style={{
              position: 'relative',
              width: '39px',
              height: '24px',
              borderRadius: '100px',
              background: showPlots ? '#6750A4' : 'rgba(219, 219, 219, 0.5)',
              padding: '1.5px 3px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: showPlots ? 'flex-end' : 'flex-start',
              transition: 'all 0.2s ease',
            }}
          >
            <div
              style={{
                width: '21px',
                height: '21px',
                borderRadius: '24px',
                background: '#FFFFFF',
                boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.3)',
                transition: 'all 0.2s ease',
              }}
            />
          </div>
        </div>

        {userData?.is_admin && (
          <div
            style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '12px',
              padding: '6px 24px',
              borderRadius: '50px',
              background: 'rgba(219, 219, 219, 0.3)',
              boxShadow: 'inset 4px 3px 5.9px 0px rgba(0, 0, 0, 0.12)',
              cursor: 'pointer',
              pointerEvents: 'auto',
            }}
            onClick={() => {
              const newIsAdminMode = !isAdminMode;
              setIsAdminMode(newIsAdminMode);
              if (newIsAdminMode) {
                // Clear gold highlight when entering god mode
                if (sceneRef.current.sprites.mapObjectHighlight && sceneRef.current.layers.highlight) {
                  sceneRef.current.layers.highlight.removeChild(sceneRef.current.sprites.mapObjectHighlight);
                  sceneRef.current.sprites.mapObjectHighlight.destroy();
                  sceneRef.current.sprites.mapObjectHighlight = null;
                  clearMapObjectHighlight();
                }
              } else {
                closeAdminRadialMenu();
                clearAdminSelection();
              }
            }}
          >
            <div
              style={{
                color: '#FFF',
                textAlign: 'center',
                WebkitTextStrokeWidth: '1.21px',
                WebkitTextStrokeColor: 'rgba(0, 0, 0, 0.60)',
                fontFamily: '"Rubik One", sans-serif',
                fontSize: '16px',
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: '29px',
                letterSpacing: '-0.747px',
                whiteSpace: 'nowrap',
              }}
            >
              God
            </div>
            <div
              style={{
                position: 'relative',
                width: '39px',
                height: '24px',
                borderRadius: '100px',
                background: isAdminMode ? '#6750A4' : 'rgba(219, 219, 219, 0.5)',
                padding: '1.5px 3px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: isAdminMode ? 'flex-end' : 'flex-start',
                transition: 'all 0.2s ease',
              }}
            >
              <div
                style={{
                  width: '21px',
                  height: '21px',
                  borderRadius: '24px',
                  background: '#FFFFFF',
                  boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.3)',
                  transition: 'all 0.2s ease',
                }}
              />
            </div>
          </div>
        )}

        {userData?.is_admin && isAdminMode && (
          <button
            onClick={async (e) => {
              e.stopPropagation();
              setIsTestingSentry(true);
              try {
                throw new Error("Test Sentry error from frontend - admin button clicked (God mode)");
              } catch (error) {
                Sentry.captureException(error);

                try {
                  await triggerErrorSentryCheckGet({ throwOnError: true });
                } catch (backendError: unknown) {
                  console.error("[Test Sentry] Backend error:", backendError);
                  Sentry.captureException(backendError);
                  showAlert(t("Sentry test completed. Check Sentry dashboard for errors."));
                }
              } finally {
                setIsTestingSentry(false);
              }
            }}
            disabled={isTestingSentry}
            style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              padding: '8px 16px',
              borderRadius: '50px',
              background: 'rgba(255, 0, 0, 0.2)',
              border: '1px solid #FF4444',
              color: '#fff',
              fontSize: '14px',
              fontWeight: 700,
              fontFamily: 'Lato, sans-serif',
              cursor: isTestingSentry ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: '2px 4px 12px rgba(255, 68, 68, 0.3)',
              marginTop: '12px',
              opacity: isTestingSentry ? 0.6 : 1,
              pointerEvents: 'auto',
            }}
            onMouseEnter={(e) => {
              if (!isTestingSentry) {
                e.currentTarget.style.background = 'rgba(255, 0, 0, 0.3)';
                e.currentTarget.style.borderColor = '#ff6666';
              }
            }}
            onMouseLeave={(e) => {
              if (!isTestingSentry) {
                e.currentTarget.style.background = 'rgba(255, 0, 0, 0.2)';
                e.currentTarget.style.borderColor = '#FF4444';
              }
            }}
          >
            {isTestingSentry ? "Testing Sentry..." : "Test Sentry"}
          </button>
        )}

        {/* Daily Quests Button */}
        <div
          style={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '53px',
            height: '53px',
            borderRadius: '50%',
            background: '#7767d7',
            boxShadow: 'inset 5px 4px 8px 0px rgba(0, 0, 0, 0.12)',
            cursor: 'pointer',
            flexShrink: 0,
            marginTop: '24px',
            pointerEvents: 'auto',
          }}
          onClick={(e) => {
            e.stopPropagation();
            openDailyQuestsModal();
          }}
        >
          {/* A newcomer who never opened the quests: a gold light runs round the button. */}
          {questsNeverOpened && hasUnfinishedQuests && <span className="quest-snake-ring" aria-hidden="true" />}
          <img
            src="/ui/daily-quests/daily-quests-map-button-icon.svg"
            alt="Daily Quests"
            style={{
              width: '30px',
              height: '30px',
              objectFit: 'contain',
            }}
          />
          {/* Exclamation mark badge - низ зображення на вертикальному центрі кнопки, максимально вліво */}
          {/* Показуємо знак оклику тільки якщо є невиконані квести */}
          {showPlots && hasUnfinishedQuests && (
            <img
              src="/ui/world-map/quest-main-marker-active.png"
              alt="!"
              style={{
                position: 'absolute',
                left: '-15px',
                bottom: '50%',
                width: '40px',
                height: '40px',
                objectFit: 'contain',
                pointerEvents: 'none',
              }}
            />
          )}
        </div>

        {/* <div
          style={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            padding: '6px 24px',
            borderRadius: '50px',
            background: 'rgba(219, 219, 219, 0.3)',
            boxShadow: 'inset 4px 3px 5.9px 0px rgba(0, 0, 0, 0.12)',
            cursor: 'pointer',
          }}
          onClick={() => setIsDailyRewardsModalOpen(true)}
        >
          <div
            style={{
              color: '#FFF',
              textAlign: 'center',
              WebkitTextStrokeWidth: '1.21px',
              WebkitTextStrokeColor: 'rgba(0, 0, 0, 0.60)',
              fontFamily: '"Rubik One", sans-serif',
              fontSize: '16px',
              fontStyle: 'normal',
              fontWeight: 400,
              lineHeight: '29px',
              letterSpacing: '-0.747px',
              whiteSpace: 'nowrap',
            }}
          >
            Daily Rewards
          </div>
        </div> */}
      </div>
    </div>
  );
}
